import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

interface GitHubInstallation {
    connectionId: string;
    creationDate: string;
    installationId: string;
    accountLogin: string;
    accountType: 'User' | 'Organization';
    repositorySelection: 'all' | 'selected';
    permissions: any;
    isActive: boolean;
    ownerEmail: string;
    name: string;
    description?: string;
    updatedAt: string;
}

export const useGitHubInstallations = () => {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: ['githubInstallations'],
        queryFn: async (): Promise<GitHubInstallation[]> => {
            const response = await apiClient.get('/integrations/github/installations');
            
            if (!response.ok) {
                throw new Error(`Failed to fetch GitHub installations: ${response.statusText}`);
            }
            
            const data = await response.json();
            return data.installations || [];
        }
    });
};

export const useInitiateGitHubAppInstallation = () => {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (tenantId: string) => {
            const response = await apiClient.get(`/integrations/github/install/${tenantId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to initiate GitHub App installation: ${response.statusText}`);
            }
            
            return response.json();
        }
    });
};

export const useUpdateGitHubInstallation = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ connectionId, updates }: { connectionId: string; updates: { name?: string; description?: string; isActive?: boolean } }) => {
            const response = await apiClient.put(`/integrations/github/installations/${connectionId}`, updates);
            
            if (!response.ok) {
                throw new Error(`Failed to update GitHub installation: ${response.statusText}`);
            }
            
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubInstallations'] });
        }
    });
};

export const useDeleteGitHubInstallation = () => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (connectionId: string) => {
            const response = await apiClient.delete(`/integrations/github/installations/${connectionId}`);
            
            if (!response.ok) {
                throw new Error(`Failed to delete GitHub installation: ${response.statusText}`);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['githubInstallations'] });
        }
    });
};